import { QueryType } from "src/types/models";

/**
 * Predicate for "is this sync auto-created and owned by Hightouch?": its
 * segment is the realtime-traits warehouse-state model or a Native Delivery
 * suppression-list model.
 *
 * This intentionally does NOT use the broader `isManagedQueryType` /
 * `ManagedQueryTypes` set: Decision Engine, Predictive, Journey, IDR, and the
 * other CEP managed models keep their own existing edit flows.
 */
export function isManagedSyncSegment(
  segment: { query_type?: string | null } | null | undefined,
): boolean {
  return (
    segment?.query_type === QueryType.RealtimeTraitWarehouseState ||
    segment?.query_type === QueryType.CepSuppressionList
  );
}

/**
 * A suppression list owns its sync, so deleting the sync alone would leave the
 * list without anything to keep it up to date.
 */
export function isSuppressionListSyncSegment(
  segment: { query_type?: string | null } | null | undefined,
): boolean {
  return segment?.query_type === QueryType.CepSuppressionList;
}

export const SUPPRESSION_LIST_SYNC_DELETE_TOOLTIP =
  "This sync cannot be deleted because it is managed by a suppression list";

export const MANAGED_MODEL_DELETE_TOOLTIP =
  "This model cannot be deleted because it is managed by Hightouch";

export type AIDManagedSegment = {
  flow_message?: {
    decision_engine_flow_id: string;
    decision_engine_message_id: string;
  } | null;
  channel_syncs?: {
    flow_id: string;
    channel?: { type: string } | null;
  } | null;
};

/**
 * Predicate for "is this sync managed by AI Decisioning?"
 *
 * This mirrors the backend `isDecisionEngineManagedSync` guard.
 */
export function isAIDManagedSegment(
  segment: AIDManagedSegment | null | undefined,
): boolean {
  return Boolean(segment?.flow_message || segment?.channel_syncs);
}

const RAW_CHANNEL_TYPE = "raw";

/**
 * Raw channels get a channel-level model from AI Decisioning, but the sync on
 * that model is created and configured by the customer rather than by AID, so
 * its configuration and schedule must stay editable.
 */
export function isRawChannelAIDSegment(
  segment: AIDManagedSegment | null | undefined,
): boolean {
  return segment?.channel_syncs?.channel?.type === RAW_CHANNEL_TYPE;
}

export const AID_MANAGED_SYNC_DELETE_TOOLTIP =
  "This sync cannot be deleted because it is managed by AI Decisioning";

export function getAIDManagedSyncHref(
  segment: AIDManagedSegment | null | undefined,
): string | null {
  if (segment?.flow_message?.decision_engine_message_id) {
    return `/ai/flows/${segment.flow_message.decision_engine_flow_id}/messages/${segment.flow_message.decision_engine_message_id}`;
  }
  if (segment?.channel_syncs?.flow_id) {
    return `/ai/flows/${segment.channel_syncs.flow_id}`;
  }
  return null;
}
